/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  IconButton,
  Divider,
  Paper,
  Avatar,
} from '@mui/material';
import { ArrowBack, Business } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import RegisterForm from '../Register/RegisterForm';
import { invitationApi } from '../../../api/invitationApi';
import { API_BASE_URL } from '../../../config/api';
import {
  InvitationDetail,
  InvitationResponse,
} from '../../../types/invitation';

interface NewUserInvitationFormProps {
  invitation: InvitationDetail;
  token: string;
  onSuccess: (
    response: InvitationResponse,
    isNewUser?: boolean,
    userEmail?: string
  ) => void;
  onError: (error: string) => void;
  onBack: () => void;
  loading?: boolean;
}

/**
 * Form component for new users to accept invitations.
 * Wraps the existing RegisterForm with invitation context.
 */
const NewUserInvitationForm: React.FC<NewUserInvitationFormProps> = ({
  invitation,
  token,
  onSuccess,
  onError,
  onBack,
  loading: _loading = false,
}) => {
  const { t } = useTranslation('studio');
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);

  // Fix relative URL for studio logo - prepend API base URL if needed
  const studioLogoUrl = React.useMemo(() => {
    if (invitation.studio_logo && !invitation.studio_logo.startsWith('http')) {
      // Use runtime API base so test deploys point at test API, not prod.
      const apiBaseUrl = API_BASE_URL;
      // Ensure the path starts with /media/
      let logoPath = invitation.studio_logo;
      if (!logoPath.startsWith('/')) {
        logoPath = `/${logoPath}`;
      }
      if (!logoPath.startsWith('/media/')) {
        logoPath = `/media${logoPath}`;
      }
      const fixedUrl = `${apiBaseUrl}${logoPath}`;
      console.log(
        '[NewUserInvitationForm] Original URL:',
        invitation.studio_logo
      );
      console.log('[NewUserInvitationForm] Fixed logo URL:', fixedUrl);
      return fixedUrl;
    }
    return invitation.studio_logo;
  }, [invitation.studio_logo]);

  // Handle successful registration
  const handleRegistrationSuccess = async () => {
    setIsAcceptingInvitation(true);

    try {
      // After successful registration, accept the invitation
      const response = await invitationApi.acceptInvitation(token, {});
      onSuccess(response, true, invitation.email); // Pass isNewUser=true and email
    } catch (error: any) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to accept invitation';
      onError(errorMessage);
    } finally {
      setIsAcceptingInvitation(false);
    }
  };

  return (
    <Box>
      {/* Invitation Context Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <IconButton
              onClick={onBack}
              size="small"
              disabled={isAcceptingInvitation}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" component="h2">
              {t('invitationForms.newUser.title')}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('invitationForms.newUser.alert', {
                studioName: invitation.studio_name,
                role: invitation.role_display,
              })}
            </Typography>
          </Alert>

          {/* Studio Information */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <Avatar
              src={studioLogoUrl || undefined}
              alt={`${invitation.studio_name} logo`}
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
              }}
              imgProps={{
                onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
                  console.error(
                    '[NewUserInvitationForm] Failed to load studio logo:',
                    studioLogoUrl,
                    e
                  );
                },
                onLoad: () => {
                  console.log(
                    '[NewUserInvitationForm] Successfully loaded studio logo:',
                    studioLogoUrl
                  );
                },
              }}
            >
              <Business />
            </Avatar>
            <Box>
              <Typography variant="h6" component="h3">
                {invitation.studio_name}
              </Typography>
              {invitation.studio_description && (
                <Typography variant="body2" color="text.secondary">
                  {invitation.studio_description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {t('invitationForms.newUser.studioInfo.invitedBy', {
                  name: invitation.invited_by_name,
                })}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Registration Form */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('invitationForms.newUser.registrationHeading')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* RegisterForm with invitation-specific props */}
        <RegisterForm
          prefilledEmail={invitation.email}
          emailDisabled
          onRegistrationSuccess={handleRegistrationSuccess}
        />
      </Paper>

      {/* Processing State */}
      {isAcceptingInvitation && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t('invitationForms.newUser.processingAlert', {
              studioName: invitation.studio_name,
            })}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default NewUserInvitationForm;
